import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Stop as StopIcon,
  PlayArrow as PlayArrowIcon,
  Replay as ReplayIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Security as SecurityIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Build as BuildIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import PageContainer from '../../components/PageContainer';
import { connectInstanceService, ConnectInstance } from '../../services/connectInstanceService';
import clientService from '../../services/clientService';
import contactService, { ImportContactJob } from '../../services/contactService';
import { Client } from '../../types';
import InstantlyAiImportModal from '../../components/InstantlyAiImportModal';
import SSHLogDrawer from '../../components/admin/SSHLogDrawer';
import ImportJobLogViewer from '../../components/ImportJobLogViewer';

const ConnectInstanceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [instance, setInstance] = useState<ConnectInstance | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [importJobs, setImportJobs] = useState<ImportContactJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [logDrawerOperation, setLogDrawerOperation] = useState<'retry' | 'cleanup' | null>(null);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<number>>(new Set());
  const [processingBulkAction, setProcessingBulkAction] = useState(false);
  const [logViewerOpen, setLogViewerOpen] = useState(false);
  const [selectedJobIdForLog, setSelectedJobIdForLog] = useState<number | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [reinstallStepDialogOpen, setReinstallStepDialogOpen] = useState(false);
  const [runStepDialogOpen, setRunStepDialogOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState('');
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [renewingCertificate, setRenewingCertificate] = useState(false);

  const validSteps = [
    'creating_user',
    'creating_directories',
    'creating_database',
    'configuring_nginx',
    'requesting_ssl',
    'creating_dns',
    'downloading_mautic',
    'installing_twig',
    'configuring_mautic',
    'initializing_schema',
    'configuring_mautic_settings',
    'rebuilding_assets',
    'creating_cron',
    'fixing_themes',
    'installing_whitelabel',
    'applying_permissions',
    'fixing_nginx',
    'fixing_messages',
    'applying_fixes',
  ];

  useEffect(() => {
    if (id) {
      fetchInstance();
    }
  }, [id]);

  useEffect(() => {
    if (instance?.client_id) {
      fetchImportJobs();
    }
  }, [instance?.client_id]);

  const fetchInstance = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const instanceData = await connectInstanceService.getOne(Number(id));
      setInstance(instanceData);
      
      // Fetch client information
      if (instanceData.client_id) {
        try {
          const clientData = await clientService.getOne(instanceData.client_id);
          setClient(clientData);
        } catch (err) {
          console.error('Error fetching client:', err);
        }
      }
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch connect instance:', err);
      setError(err.message || 'Failed to load connect instance');
    } finally {
      setLoading(false);
    }
  };

  const fetchImportJobs = async () => {
    if (!instance?.client_id) {
      console.log('fetchImportJobs: No client_id available', { instance });
      return;
    }
    try {
      setLoadingJobs(true);
      setJobsError(null);
      console.log('fetchImportJobs: Fetching jobs for client_id:', instance.client_id);
      const jobs = await contactService.getImportJobs(instance.client_id);
      console.log('fetchImportJobs: Received jobs:', jobs);
      setImportJobs(jobs || []);
      if (!jobs || jobs.length === 0) {
        console.log('fetchImportJobs: No jobs returned from API');
      }
    } catch (err: any) {
      console.error('Failed to fetch import jobs:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load import jobs';
      setJobsError(errorMsg);
      setImportJobs([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleImportSuccess = () => {
    console.log('handleImportSuccess: Refreshing import jobs list');
    // Small delay to ensure backend has committed the job
    setTimeout(() => {
      fetchImportJobs();
    }, 500);
  };

  const handleStopJob = async (jobId: number) => {
    if (!instance?.client_id) return;
    try {
      await contactService.stopImportJob(jobId, instance.client_id);
      await fetchImportJobs();
    } catch (err: any) {
      console.error('Failed to stop job:', err);
      alert(err.message || 'Failed to stop job');
    }
  };

  const handleRestartJob = async (jobId: number) => {
    if (!instance?.client_id) return;
    try {
      await contactService.restartImportJob(jobId, instance.client_id);
      await fetchImportJobs();
    } catch (err: any) {
      console.error('Failed to restart job:', err);
      alert(err.message || 'Failed to restart job');
    }
  };

  const handleRetryJob = async (jobId: number) => {
    if (!instance?.client_id) return;
    try {
      await contactService.retryImportJob(jobId, instance.client_id);
      await fetchImportJobs();
    } catch (err: any) {
      console.error('Failed to retry job:', err);
      alert(err.message || 'Failed to retry job');
    }
  };

  const handleSelectJob = (jobId: number, selected: boolean) => {
    setSelectedJobIds((prev) => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(jobId);
      } else {
        newSet.delete(jobId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedJobIds(new Set(importJobs.map(job => job.id)));
    } else {
      setSelectedJobIds(new Set());
    }
  };

  const handleBulkStop = async () => {
    if (!instance?.client_id || selectedJobIds.size === 0) return;
    if (!confirm(`Stop ${selectedJobIds.size} job(s)?`)) return;
    
    try {
      setProcessingBulkAction(true);
      const promises = Array.from(selectedJobIds).map(jobId => 
        contactService.stopImportJob(jobId, instance.client_id!)
      );
      await Promise.all(promises);
      setSelectedJobIds(new Set());
      await fetchImportJobs();
    } catch (err: any) {
      console.error('Failed to stop jobs:', err);
      alert(err.message || 'Failed to stop some jobs');
    } finally {
      setProcessingBulkAction(false);
    }
  };

  const handleBulkResume = async () => {
    if (!instance?.client_id || selectedJobIds.size === 0) return;
    if (!confirm(`Resume ${selectedJobIds.size} job(s)?`)) return;
    
    try {
      setProcessingBulkAction(true);
      const promises = Array.from(selectedJobIds).map(jobId => 
        contactService.restartImportJob(jobId, instance.client_id!)
      );
      await Promise.all(promises);
      setSelectedJobIds(new Set());
      await fetchImportJobs();
    } catch (err: any) {
      console.error('Failed to resume jobs:', err);
      alert(err.message || 'Failed to resume some jobs');
    } finally {
      setProcessingBulkAction(false);
    }
  };

  const handleBulkRestart = async () => {
    if (!instance?.client_id || selectedJobIds.size === 0) return;
    if (!confirm(`Restart ${selectedJobIds.size} job(s)? This will reset progress and start from the beginning.`)) return;
    
    try {
      setProcessingBulkAction(true);
      const promises = Array.from(selectedJobIds).map(jobId => 
        contactService.retryImportJob(jobId, instance.client_id!)
      );
      await Promise.all(promises);
      setSelectedJobIds(new Set());
      await fetchImportJobs();
    } catch (err: any) {
      console.error('Failed to restart jobs:', err);
      alert(err.message || 'Failed to restart some jobs');
    } finally {
      setProcessingBulkAction(false);
    }
  };

  const getSelectedJobs = () => {
    return importJobs.filter(job => selectedJobIds.has(job.id));
  };

  const canStopSelected = () => {
    return getSelectedJobs().some(job => job.status === 'running' || job.status === 'processing' || job.status === 'in_progress');
  };

  const canResumeSelected = () => {
    return getSelectedJobs().some(job => job.status === 'stopped' || job.status === 'failed');
  };

  const canRestartSelected = () => {
    return getSelectedJobs().some(job => job.status === 'stopped' || job.status === 'failed' || job.status === 'completed');
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await contactService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'instantly-ai-import-template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Failed to download template:', err);
      alert(err.message || 'Failed to download template');
    }
  };

  const handleRetry = async () => {
    if (!id) return;
    try {
      await connectInstanceService.retry(Number(id));
      setLogDrawerOperation('retry');
      setLogDrawerOpen(true);
      await fetchInstance();
    } catch (err: any) {
      alert(err.message || 'Failed to retry instance');
    }
  };

  const handleStop = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to stop this instance installation?')) return;
    try {
      await connectInstanceService.stop(Number(id));
      await fetchInstance();
      setActionMenuAnchor(null);
    } catch (err: any) {
      alert(err.message || 'Failed to stop instance');
    }
  };

  const handleCleanup = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to cleanup and reset this instance? This will reset the installation process.')) return;
    try {
      await connectInstanceService.cleanup(Number(id));
      setLogDrawerOperation('cleanup');
      setLogDrawerOpen(true);
      await fetchInstance();
      setActionMenuAnchor(null);
    } catch (err: any) {
      alert(err.message || 'Failed to cleanup instance');
    }
  };

  const handleCheckHealth = async () => {
    if (!id) return;
    try {
      setCheckingHealth(true);
      await connectInstanceService.checkHealth(Number(id));
      await fetchInstance();
    } catch (err: any) {
      alert(err.message || 'Failed to check health');
    } finally {
      setCheckingHealth(false);
    }
  };

  const handleRenewCertificate = async () => {
    if (!id) return;
    if (!confirm('Are you sure you want to renew the SSL certificate?')) return;
    try {
      setRenewingCertificate(true);
      await connectInstanceService.renewCertificate(Number(id));
      await fetchInstance();
    } catch (err: any) {
      alert(err.message || 'Failed to renew certificate');
    } finally {
      setRenewingCertificate(false);
    }
  };

  const handleReinstallFromStep = async () => {
    if (!id || !selectedStep) return;
    try {
      await connectInstanceService.reinstallFromStep(Number(id), selectedStep);
      setReinstallStepDialogOpen(false);
      setSelectedStep('');
      setLogDrawerOpen(true);
      setLogDrawerOperation(null);
      await fetchInstance();
    } catch (err: any) {
      alert(err.message || 'Failed to reinstall from step');
    }
  };

  const handleRunStep = async () => {
    if (!id || !selectedStep) return;
    try {
      await connectInstanceService.runStep(Number(id), selectedStep);
      setRunStepDialogOpen(false);
      setSelectedStep('');
      setLogDrawerOpen(true);
      setLogDrawerOperation(null);
      await fetchInstance();
    } catch (err: any) {
      alert(err.message || 'Failed to run step');
    }
  };

  const handleViewLogs = () => {
    setLogDrawerOpen(true);
    setLogDrawerOperation(null);
    setActionMenuAnchor(null);
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'running':
      case 'processing':
      case 'in_progress':
        return 'info';
      case 'failed':
      case 'error':
        return 'error';
      case 'stopped':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const canRetry = instance?.status === 'failed' || instance?.status === 'stopped';
  const canStop = instance?.status === 'in_progress' || instance?.status === 'pending';
  const canCleanup = instance?.status !== 'completed';

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (error || !instance) {
    return (
      <PageContainer>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Connect instance not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/admin/connect-instances')} sx={{ mt: 2 }}>
          Back to Connect Instances
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/app/admin/connect-instances')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">Connect Instance Details</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchInstance}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={(e) => setActionMenuAnchor(e.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Client Information */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Client Information
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Client ID
                </Typography>
                <Typography variant="body1">{instance.client_id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Agency Name
                </Typography>
                <Typography variant="body1">{client?.agency_name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Domain
                </Typography>
                <Typography variant="body1">{instance.domain}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={instance.status}
                  color={getStatusColor(instance.status) as any}
                  size="small"
                />
              </Grid>
              {instance.current_step && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Step
                  </Typography>
                  <Typography variant="body1">{instance.current_step}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Database Name
                </Typography>
                <Typography variant="body1">{instance.db_name || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Database User
                </Typography>
                <Typography variant="body1">{instance.db_user || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1">{instance.username || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Health Status
                </Typography>
                <Chip
                  label={instance.health_status || 'Unknown'}
                  color={instance.health_status === 'healthy' ? 'success' : 'default'}
                  size="small"
                />
              </Grid>
              {instance.certificate_expires_at && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Certificate Expires
                  </Typography>
                  <Typography variant="body1">{formatDate(instance.certificate_expires_at)}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body1">{formatDate(instance.created_at)}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Install Status & Progress */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Install Status & Progress</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {canRetry && (
                  <Button
                    variant="contained"
                    startIcon={<ReplayIcon />}
                    onClick={handleRetry}
                    color="primary"
                  >
                    Retry
                  </Button>
                )}
                {canStop && (
                  <Button
                    variant="outlined"
                    startIcon={<StopIcon />}
                    onClick={handleStop}
                    color="error"
                  >
                    Stop
                  </Button>
                )}
                {canCleanup && (
                  <Button
                    variant="outlined"
                    startIcon={<BuildIcon />}
                    onClick={handleCleanup}
                    color="warning"
                  >
                    Cleanup
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={handleViewLogs}
                >
                  View Logs
                </Button>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            
            {instance.error_message && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Error:</Typography>
                <Typography variant="body2">{instance.error_message}</Typography>
              </Alert>
            )}

            {instance.creation_log && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Creation Log (last 500 characters):
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'auto',
                    fontSize: '0.75rem',
                    maxHeight: '200px',
                  }}
                >
                  {instance.creation_log.slice(-500)}
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<HealthAndSafetyIcon />}
                onClick={handleCheckHealth}
                disabled={checkingHealth}
                size="small"
              >
                {checkingHealth ? 'Checking...' : 'Check Health'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SecurityIcon />}
                onClick={handleRenewCertificate}
                disabled={renewingCertificate}
                size="small"
              >
                {renewingCertificate ? 'Renewing...' : 'Renew Certificate'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<BuildIcon />}
                onClick={() => {
                  setSelectedStep('');
                  setReinstallStepDialogOpen(true);
                }}
                size="small"
              >
                Reinstall From Step
              </Button>
              <Button
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => {
                  setSelectedStep('');
                  setRunStepDialogOpen(true);
                }}
                size="small"
              >
                Run Specific Step
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Contacts & Import Jobs */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Contacts & Import Jobs</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Download CSV Template">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadTemplate}
                  >
                    Template
                  </Button>
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => setImportModalOpen(true)}
                  disabled={!instance.client_id}
                >
                  Load Instantly.ai Contacts
                </Button>
                <IconButton onClick={fetchImportJobs} size="small">
                  <RefreshIcon />
                </IconButton>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />

            {/* Bulk Actions */}
            {selectedJobIds.size > 0 && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2">
                  {selectedJobIds.size} job(s) selected
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<StopIcon />}
                  onClick={handleBulkStop}
                  disabled={processingBulkAction || !canStopSelected()}
                  color="error"
                >
                  Stop
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleBulkResume}
                  disabled={processingBulkAction || !canResumeSelected()}
                  color="primary"
                >
                  Resume
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ReplayIcon />}
                  onClick={handleBulkRestart}
                  disabled={processingBulkAction || !canRestartSelected()}
                  color="primary"
                >
                  Restart
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setSelectedJobIds(new Set())}
                >
                  Clear Selection
                </Button>
              </Box>
            )}

            {loadingJobs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : jobsError ? (
              <Alert severity="error">
                <Typography variant="body2">Error loading import jobs: {jobsError}</Typography>
                <Button size="small" onClick={fetchImportJobs} sx={{ mt: 1 }}>
                  Retry
                </Button>
              </Alert>
            ) : importJobs.length === 0 ? (
              <Alert severity="info">No import jobs found. Click "Load Instantly.ai Contacts" to start an import.</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedJobIds.size > 0 && selectedJobIds.size < importJobs.length}
                          checked={importJobs.length > 0 && selectedJobIds.size === importJobs.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Filename</TableCell>
                      <TableCell>Tag Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Progress</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Updated</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importJobs.map((job) => (
                        <TableRow key={job.id} selected={selectedJobIds.has(job.id)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedJobIds.has(job.id)}
                              onChange={(e) => handleSelectJob(job.id, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>{job.id}</TableCell>
                          <TableCell>{job.filename}</TableCell>
                          <TableCell>{job.tag_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={job.status}
                              color={getStatusColor(job.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {job.total_rows > 0 ? (
                              <Box>
                                <Typography variant="body2">
                                  {job.processed_rows} / {job.total_rows} ({Math.round((job.processed_rows / job.total_rows) * 100)}%)
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Created: {job.created_count} | Updated: {job.updated_count} | Skipped: {job.skipped_count}
                                </Typography>
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell>{formatDate(job.created_at)}</TableCell>
                          <TableCell>{formatDate(job.updated_at)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="View Log">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedJobIdForLog(job.id);
                                    setLogViewerOpen(true);
                                  }}
                                  color="primary"
                                >
                                  <DescriptionIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {job.s3_key && (
                                <Tooltip title="Download CSV file">
                                  <IconButton
                                    size="small"
                                    onClick={async () => {
                                      try {
                                        // Fetch file content from backend and trigger download
                                        const response = await contactService.getImportJobFileContent(job.id, instance.client_id);
                                        
                                        // Create blob and trigger download
                                        const blob = new Blob([response], { type: 'text/csv' });
                                        const downloadUrl = window.URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = downloadUrl;
                                        link.download = job.filename || `import-job-${job.id}.csv`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        window.URL.revokeObjectURL(downloadUrl);
                                      } catch (err: any) {
                                        console.error('Failed to download file:', err);
                                        alert(err.response?.data?.detail || err.message || 'Failed to download file');
                                      }
                                    }}
                                    color="primary"
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {job.status === 'running' || job.status === 'processing' || job.status === 'in_progress' ? (
                                <Tooltip title="Stop Job">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleStopJob(job.id)}
                                    color="error"
                                  >
                                    <StopIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ) : job.status === 'stopped' || job.status === 'failed' ? (
                                <>
                                  <Tooltip title="Resume Job">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRestartJob(job.id)}
                                      color="primary"
                                    >
                                      <PlayArrowIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Restart Job">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRetryJob(job.id)}
                                      color="primary"
                                    >
                                      <ReplayIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ) : null}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem onClick={handleViewLogs}>
          <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
          View Logs
        </MenuItem>
        {canRetry && (
          <MenuItem onClick={handleRetry}>
            <ReplayIcon sx={{ mr: 1 }} fontSize="small" />
            Retry
          </MenuItem>
        )}
        {canStop && (
          <MenuItem onClick={handleStop}>
            <StopIcon sx={{ mr: 1 }} fontSize="small" />
            Stop
          </MenuItem>
        )}
        {canCleanup && (
          <MenuItem onClick={handleCleanup}>
            <BuildIcon sx={{ mr: 1 }} fontSize="small" />
            Cleanup
          </MenuItem>
        )}
        <MenuItem onClick={handleCheckHealth} disabled={checkingHealth}>
          <HealthAndSafetyIcon sx={{ mr: 1 }} fontSize="small" />
          Check Health
        </MenuItem>
        <MenuItem onClick={handleRenewCertificate} disabled={renewingCertificate}>
          <SecurityIcon sx={{ mr: 1 }} fontSize="small" />
          Renew Certificate
        </MenuItem>
      </Menu>

      {/* Reinstall From Step Dialog */}
      <Dialog open={reinstallStepDialogOpen} onClose={() => setReinstallStepDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reinstall From Step</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Step</InputLabel>
            <Select
              value={selectedStep}
              onChange={(e) => setSelectedStep(e.target.value)}
              label="Step"
            >
              {validSteps.map((step) => (
                <MenuItem key={step} value={step}>
                  {step}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReinstallStepDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReinstallFromStep} variant="contained" disabled={!selectedStep}>
            Reinstall
          </Button>
        </DialogActions>
      </Dialog>

      {/* Run Step Dialog */}
      <Dialog open={runStepDialogOpen} onClose={() => setRunStepDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run Specific Step</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Step</InputLabel>
            <Select
              value={selectedStep}
              onChange={(e) => setSelectedStep(e.target.value)}
              label="Step"
            >
              {validSteps.map((step) => (
                <MenuItem key={step} value={step}>
                  {step}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunStepDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRunStep} variant="contained" disabled={!selectedStep}>
            Run Step
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Modal */}
      {instance.client_id && (
        <InstantlyAiImportModal
          open={importModalOpen}
          onClose={() => {
            setImportModalOpen(false);
            // Refresh jobs list when modal closes (in case import completed)
            setTimeout(() => fetchImportJobs(), 1000);
          }}
          clientId={instance.client_id}
          onSuccess={handleImportSuccess}
        />
      )}

      {/* SSH Log Drawer */}
      <SSHLogDrawer
        open={logDrawerOpen}
        instanceId={instance.id}
        operationType={logDrawerOperation}
        onClose={() => {
          setLogDrawerOpen(false);
          setLogDrawerOperation(null);
          fetchInstance();
        }}
      />

      {/* Import Job Log Viewer */}
      {instance.client_id && (
        <ImportJobLogViewer
          open={logViewerOpen}
          jobId={selectedJobIdForLog}
          clientId={instance.client_id}
          onClose={() => {
            setLogViewerOpen(false);
            setSelectedJobIdForLog(null);
            fetchImportJobs();
          }}
        />
      )}
    </PageContainer>
  );
};

export default ConnectInstanceDetail;
